import {
  AgendaItemCategory,
  BillingPackageID,
  BillingProductType,
  BoardMember,
  CredentialEnum,
  DiscountItem,
  DiscountType,
  EarlyAccessEnum,
  ExtraPaidService,
  getUserFriendlyRolesEnumeration,
  IdentityRecord,
  IdentityType,
  ImportUser,
  IOrganization,
  IUser,
  MeetingType,
  MongoId,
  OrderedSubscriptionDetail,
  OrgAndClientLogoType,
  OrganizationSubscription,
  OrgLanguage,
  PackagePrice,
  PaidExtraService,
  ProductSchema,
  ReinvoicingRadioButtonType,
  UserManualId,
} from '@orgbrain/lib-data';
import {
  _defaultRoleMap,
  ClientUserRole,
  CompanyAcronymsEnum,
  hasPermission,
  PermissionEnum,
  Role,
  RoleByTime,
  RoleContext,
  RoleMapType,
  rolesPriorityMap,
  sortByRoleHelper,
  TranslationTextMap,
} from '@orgbrain/roles';
import momentTimezone from 'moment-timezone';
import { AgendaItemAttachment } from './agendaitem.class';
import { PACKAGE_CATEGORY, packageMap } from './organizationSubscriptionData';
import { roundToPrecision } from './util';
import { DecisionVote, ElectionCandidate, ElectionVote, GAMajorityRequirementRule } from './voting';

const RESONABLY_BIG_NUMBER = 999; // That is not very BIG !!
export const TIME_UNTIL_MEETING_START_TO_CONFIRM_ATTENDANCE_MS = 30 * 60 * 1000;

export function setMfaMethod({
  methods,
  method,
  yes,
}: {
  methods: CredentialEnum[];
  method: CredentialEnum;
  yes: boolean;
}) {
  const ii = methods.indexOf(method);
  if (ii !== -1) {
    if (yes) return;
    methods.splice(ii, 1);
  } else {
    if (!yes) return;
    methods.push(method);
  }
}

export enum MeetingNoteType {
  PRIVATE_NOTE = 'PRIVATE_NOTE',
  PUBLIC_NOTE = 'PUBLIC_NOTE',
  MINUTES_NOTE = 'MINUTES_NOTE',
}

export const gaOnlyMeetingTypes: MeetingType[] = [
  MeetingType.ANNUAL_GENERAL_MEETING,
  MeetingType.EXTRAORDINARY_GENERAL_MEETING,
  MeetingType.SHAREHOLDER_MEETING,
];

export enum AgendaMinutesChangeType {
  FULL_UPDATE = 'FULL_UPDATE',
  ADD = 'ADD',
  MOVE = 'MOVE',
  DELETE = 'DELETE',
  APPEND_MULTIPLE = 'APPEND_MULTIPLE',
  AGENDA_FIELD_UPDATE = 'AGENDA_FIELD_UPDATE',
  MINUTES_FIELD_UPDATE = 'MINUTES_FIELD_UPDATE',
  UNKNOWN_FIELD_UPDATE = 'UNKNOWN_FIELD_UPDATE',
  VOTES_UPDATE = 'VOTES_UPDATE',
  ELECTION_UPDATE = 'ELECTION_UPDATE',
  VOTING_LOCKED_STATE_UPDATE = 'VOTING_LOCKED_STATE_UPDATE',
  GA_MAJORITY_REQUIREMENT_RULE_UPDATE = 'GA_MAJORITY_REQUIREMENT_RULE_UPDATE',
  AGENDA_GENERAL_COMMENTS_UPDATE = 'AGENDA_GENERAL_COMMENTS_UPDATE',
  MINUTES_GENERAL_COMMENTS_UPDATE = 'MINUTES_GENERAL_COMMENTS_UPDATE',
  MINUTES_APPROVALS_UPDATE = 'MINUTES_APPROVALS_UPDATE',
  AGENDA_RESTRICTED_LIST = 'AGENDA_RESTRICTED_LIST',
  AGENDA_ALLOWED_LIST = 'AGENDA_ALLOWED_LIST',
  MINUTES_MASS_GEMERATION_SETTINGS_UPDATE = 'MINUTES_MASS_GEMERATION_SETTINGS_UPDATE',
}

export enum BOARDMEETING_STATUS {
  DRAFT = 'DRAFT',
  PLANNED = 'PLANNED',
  CALLED = 'CALLED',
  ANNUAL_PLAN = 'ANNUAL_PLAN',
  ACTIVE = 'ACTIVE',
  REQUIRES_MINUTES = 'REQUIRES_MINUTES',
  REQUIRES_DISCUSSION = 'REQUIRES_DISCUSSION',
  REQUIRES_SIGNATURES = 'REQUIRES_SIGNATURES',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}
export const BOARDMEETING_BEFORE_MINUTES_GENERATED = [
  BOARDMEETING_STATUS.DRAFT,
  BOARDMEETING_STATUS.PLANNED,
  BOARDMEETING_STATUS.CALLED,
  BOARDMEETING_STATUS.ANNUAL_PLAN,
  BOARDMEETING_STATUS.ACTIVE,
  BOARDMEETING_STATUS.REQUIRES_MINUTES,
];

export const BOARDMEETING_BEFORE_END_STATUSES = [
  BOARDMEETING_STATUS.DRAFT,
  BOARDMEETING_STATUS.PLANNED,
  BOARDMEETING_STATUS.CALLED,
  BOARDMEETING_STATUS.ANNUAL_PLAN,
  BOARDMEETING_STATUS.ACTIVE,
];
export const BOARDMEETING_BEFORE_MEETING_START = [
  BOARDMEETING_STATUS.DRAFT,
  BOARDMEETING_STATUS.PLANNED,
  BOARDMEETING_STATUS.CALLED,
  BOARDMEETING_STATUS.ANNUAL_PLAN,
];
export enum MeetingAttendenceStatusResponse {
  ATTENDING = 'ATTENDING',
  DIGITAL_ATTENDANCE = 'DIGITAL_ATTENDANCE',
  NOT_ATTENDING = 'NOT_ATTENDING',
  PROXY = 'PROXY',
}
export enum MINUTES_STEP_STATUS {
  MINUTES_INITIAL = 'MINUTES_INITIAL',
  MINUTES_DRAFT = 'MINUTES_DRAFT',
  MINUTES_DISCUSSION = 'MINUTES_DISCUSSION',
  MINUTES_SIGNING = 'MINUTES_SIGNING',
  MINUTES_COMPLETED = 'MINUTES_COMPLETED',
}

export enum ProcedureType {
  PHYSICAL_MEETING = 'PHYSICAL_MEETING',
  PHONE_MEETING = 'PHONE_MEETING',
  VIDEO_CONFERENCE = 'VIDEO_CONFERENCE',
  HYBRID_MEETING = 'HYBRID_MEETING',
  DIGITAL_MEETING = 'DIGITAL_MEETING',
  WRITTEN_PROCEDURE = 'WRITTEN_PROCEDURE',
  OTHER_PROCEDURE = 'OTHER_PROCEDURE',
}

export class ClientSummary {
  clientId: string;
  name: string;
  logo?: OrgAndClientLogoType;
  useClientName?: boolean;
  useCustomBilling?: boolean;
  // used to associate a landing page with a client
  orderKey?: string;
}

export class ClientMemberDetail {
  _id?: string;
  acceptTerms: boolean;
  createdAt: Date;
  email: string;
  firstName: string;
  lastName: string;
  sub: string;
  superuser: boolean;
  updatedAt: Date;
  roles: ClientUserRole[];
  deleted?: boolean;
  sendInvite: any;
  dirty: boolean;
}

export class Client extends ClientSummary {
  _id?: string;
  members: ClientMemberDetail[] = []; // TODO is this BaseUser?
  organizations: Organization[];
}

export type KeyValuePair = {
  key: string;
  value: string;

  // For checkboxes
  selected?: boolean;
};

export type StringKeyBooleanValue = {
  [key: string]: string;
};

export class AgendaItemSuggestion {
  id?: string;
  organization?: string;
  language?: OrgLanguage;
  suggestedForMeetingTypes: MeetingType[];
  suggestedForOrgTypes: string[];
  agendaSuggestionTopics: string[];
  sortOrder: number;
  title: string;
  category: AgendaItemCategory;
  description: string;
  proposedResolution?: string;
  gaMajorityRequirementRule?: string;
  electionNCandidatesToElect: Number;
  minuteDefaultText?: string;
  helpText?: string;
  createdBy: string;
  createdAt: string;
  isWinnerSigningPdf?: boolean;

  constructor(sortOrder = RESONABLY_BIG_NUMBER) {
    this.suggestedForMeetingTypes = [];
    this.suggestedForOrgTypes = [];
    this.agendaSuggestionTopics = [];
    this.category = AgendaItemCategory.INFORMATION;
    this.sortOrder = sortOrder;
  }
}

export type ValueLabelPair = {
  value: string;
  label: string;
  tooltipText?: string;
  searchableText?: string;
  //optional right aligned label
  secondaryLabel?: string;
  // For checkboxes
  selected?: boolean;
};

export class CreateEntityHint {
  tax?: boolean;

  //   {
  //     tax_shareholder: string;
  //     tax_dob_orgno: string;
  //     tax_address: string;
  //   };
  generic?: boolean;

  //   {
  //     // isCompany: boolean;
  //     // dob: string;
  //     // orgnr: string;
  //   };
  bisnode?: boolean;

  //   {
  //     isCompany: boolean;
  //     internref: string;
  //     orgnr: string;
  //   };
}

export class UserHint {
  // if data from  entity
  first_name?: string;
  last_name?: string;
  internref?: string; // Bisnode reference

  email?: string;
  phone?: string;
}

// LINK  ../../../../libs/lib-mongoose/src/lib/user-schema.ts#USER_SCHEMA
// ANCHOR[id=USER_CLASS]

export class PrimitiveUser {
  firstName = '';
  lastName = '';
  email = '';
}

function parsingNameHelper(bigString): string[] {
  const tokas = bigString
    .split(' ')
    .map((tok) => tok.trim())
    .filter((tok) => !!tok)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1));

  let len = tokas.length - 1;

  if (len < 1) {
    len = 1;
  }
  return [tokas.slice(0, len).join(' '), tokas.slice(len).join(' ')];
}

// Base user has the data from the user record ( see current user for user after we sewt organizations etc.)
// This probably should deprecated and use  IUser
export class BaseUser extends PrimitiveUser implements IUser {
  id?: string;
  _id?: any;

  // Admin controlled variable
  phone?: string;

  // DEPRECATE ME
  // phoneStamp?: string;
  // bankidUser?: any;
  // bankidStamp?: string;
  // bankidSub?: string; // associated bankid
  // ---------------------------
  createdBy: MongoId;
  lang?: 'nb' | 'en' | string; // I think this is: nb or en
  logo?: OrgAndClientLogoType;
  mfaMethods: CredentialEnum[]; // Therde are the user login MFA methods
  zombie?: boolean; // after transfered invite set to true,
  code?: string; // SMS code store
  codestamp?: number; //  time stamp for SMS code.
  superuser: boolean;
  superclient?: boolean;
  supportuser?: boolean;
  sendInvite?: boolean;
  acceptUsageTracking?: Date;
  hasSeenUsageTrackingDialog?: Date;
  acceptTerms: boolean;
  acceptMarketingEmail: boolean;
  openaiPermission?: any;
  // PJL_LATER_TODO   mode into identities
  sub: string;

  internref?: string = null; // Bisnode reference
  dirty?: boolean;
  mergedinto?: string; // used to transfer
  deleted?: boolean;
  temp?: any;
  tempPassword?: string;
  invited: any;
  createdAt: string;
  updatedAt: string;
  logonAttempts?: number; // back end securitity
  logonStamp?: number; // back end securitity
  addToGoogleCalendar?: boolean;
  totpSecret?: string; //  should not escape from backend
  newPhone?: string;
  identities?: IdentityRecord[];
  timeZone?: string;

  static fullname(user: BaseUser): string {
    if (!user) {
      return '';
    }
    let ret = '';

    if (user.firstName) {
      ret += user.firstName;
    }
    if (user.lastName) {
      ret += ' ' + user.lastName;
    }

    ret = ret.trim();
    if (ret.length > 0) {
      return ret;
    }
    return user.email;
  }

  static commaSeparatedUserFix(user: PrimitiveUser) {
    user = user || ({} as any);
    let fName = (user.firstName || '').replace(/[\s+,]+/g, ' ').trim();
    let lName = (user.lastName || '').replace(/[\s+,]+/g, ' ').trim();

    if (lName && !fName) {
      const result = parsingNameHelper(lName);
      fName = result[0];
      lName = result[1];
    } else if (fName && !lName) {
      const result = parsingNameHelper(fName);
      fName = result[0];
      lName = result[1];
    }
    user.firstName = fName;
    user.lastName = lName;
  }

  static searchForUsernameMatch(existing: BaseUser[], newUser: BaseUser): BaseUser[] {
    const list = existing.filter(
      (e) =>
        e.firstName.toLowerCase() === newUser.firstName.toLowerCase() &&
        e.lastName.toLowerCase() === newUser.lastName.toLowerCase()
    );
    return list;
  }
}

// Not sure about this one .  Seems like sometimes we will not have rolesByTime.  Mybe see if IUser is what we want.
export class User extends BaseUser {
  roles: Role[];
  rolesByTime: RoleByTime[];
  // Temporary
  added?: string;
  manualId?: UserManualId;
  static clone(user: User): User {
    const ret = structuredClone(user);
    return ret;
  }
}

export enum CognitoUserStatusEnum {
  CONFIRMED = 'CONFIRMED',
  FORCE_CHANGE_PASSWORD = 'FORCE_CHANGE_PASSWORD',
}

export class UserPreregData {
  firstName: string;
  lastName: string;
  email: string;
  userId: any;
  phoneRequired: boolean;
  phone: string;
  organizationName: string;
  client: string;
  UserStatus: string;
  acceptTerms: boolean;
  identities: IdentityType[];
}

export type CognitoTokenData = {
  AuthenticationResult: { AccessToken: string };
};
export class UserData {
  userId: string;
  sub: string;
  firstName: string;
  lastName: string;
  Username: string;
  UserCreateDate: string;
  UserLastModifiedDate: string;
  Enabled: boolean;
  UserStatus: string;
  email_verified: boolean;
  name: string;
  // hacky add ons not really part of cognito
  email?: string;
  phone?: string;
  phoneRequired?: boolean;
  // unverified phone
  newPhone?: string;
  organizationName?: any;
  password?: string;
}

export interface CognitoUserData {}

export type IdMap = {
  [id: string]: boolean;
};

export type Session = {
  credentials: { [id in CredentialEnum]?: string }; //  This  is an iso time stamp of when id was asserted.
  updatedAt: Date;
  createdAt: Date;
  lastActivity?: string; // front and back end have a different idea of what this is!!!
  expires?: string;
  slidePeriod?: number;
  inactivityPeriod: { [cred in CredentialEnum]?: number };
  id: string;
};

// Current user is a user with roles and organizations. The backend fills in the roles and organizations.
// So backend req.user_data   and also the current user in front end.
export class CurrentUser extends BaseUser {
  client_admin: IdMap;
  clientRoles: { [key: string]: { [roleType in ClientUserRole]: boolean } };
  organizations: OrganizationWithRoles[];
  roles: { [orgId: string]: { [role: string]: boolean } };
  // organizationToRoleMap: { [organizationId: string]: RoleMapType };
  saveSuperBeforeItGetsDeleted?: boolean; // HOrrible hack sorry
  manualIdMap?: { [organizationId: string]: UserManualId };
  orgIdToOrganizationIdMap: { [orgId: string]: string };
  earlyAccess?: { [earlyAccessKey in EarlyAccessEnum]: boolean };
  canbesuper?: boolean;
  alien?: boolean; // added by backe/Applications/Visual Studio Code.app/Contents/Resources/app/out/vs/code/electron-sandbox/workbench/workbench.htmlnd on the fly.
  session?: Session;
  invitationTimestamp?: Date;
  // used during onboarding to hold the import informations
  importUser?: ImportUser;
  emailValidation?: { validationCode: any; expiresAt: number; oldEmail: string; newEmail: any };
  tempPasswordTimeout?: number; // unix time stamp  (Date.now() etc)
}

export class MeetingInvitee {
  user: string;
  status: MeetingInviteeStatus;
  grantVotingRights?: boolean;
  attendingWithVisiting?: boolean;
  deleted?: boolean;
}

export class MeetingShareholderInvitee {
  // Note that user is not used because instead we use user representative from the Entity
  user: string;
  status: MeetingInviteeStatus;
  entity: string;
  proxyEmail?: string;
  proxyWelcomeText?: string;
  proxyUploadedAttachment?: string;
  proxyId?: string;
  proxyName?: string;
  hasConfirmedAttendance?: boolean;
  attendingPhysical?: boolean;
  attendingWithProxy?: boolean;
  attendingWithVisiting?: boolean;
  chairmanAsProxy?: boolean;
  chairmanId?: string;
  representativesAtTime?: BaseUser[];
  statusRecordedBy?: string;
  grantVotingRights?: boolean;
  preVotingRecorded?: boolean;
  createdAt?: string;
  updatedAt?: string;
  deleted?: boolean;
}

export enum MeetingStatusUpdateType {
  INVITATION = 'INVITATION',
  ATTENDANCE_STATUS_CHAGNE = 'ATTENDANCE_STATUS_CHAGNE',
  PROXY_CONFIGURATION = 'PROXY_CONFIGURATION',
  PRE_VOTING_CONFIGURATION = 'PRE_VOTING_CONFIGURATION',
  REMOVE_FROM_INVITED = 'REMOVE_FROM_INVITED',
  MEETING_SET_ACTIVE = 'MEETING_SET_ACTIVE',
}

export enum MeetingInviteeStatus {
  PENDING_ANSWER = 'PENDING_ANSWER',
  ATTENDING = 'ATTENDING',
  ATTENDING_VIA_VIDEO = 'ATTENDING_VIA_VIDEO',
  NOT_ATTENDING = 'NOT_ATTENDING',
}

export enum TemplateTagType {
  INPUT_STRING = 'INPUT_STRING',
  INPUT_NUMBER = 'INPUT_NUMBER',
  INPUT_DATE = 'INPUT_DATE',
  SELECT_OPTION = 'SELECT_OPTION',
  SELECT_ONE_OR_EMPTY = 'SELECT_ONE_OR_EMPTY',
}

export const VAR_KEY = '{{var}}';

export const SELECT_OPTION_CUSTOM = 'SELECT_OPTION_CUSTOM';

/**
 * Deprecated
 * Use IOrganization from @orgbrain/lib-data instead.
 * This type alias is kept for backward compatibility.
 */
export type OrganizationSummary = IOrganization;

/**
 * Organization combines IOrganization (DB fields) with computed/frontend-only fields.
 * - IOrganization: All database-persisted fields (defined in lib-data)
 *
 * Use IOrganization when you only need DB fields.
 * Use Organization when you need the full object with computed fields.
 */
export interface Organization extends IOrganization {}

// eslint-disable-next-line no-redeclare
export class Organization {
  // IOrganization fields - using declaration merging, we only need to declare
  // fields that need default values. TypeScript allows class to satisfy interface
  // through runtime assignment.

  // Fields with default values
  orgId: string = null;
  name: string = null;
  clientId: string = null;

  // OrganizationComputedFields
  onlyForToolbar?: boolean;
  members?: Array<User> = [];
  roleMap?: RoleMapType;
  availablePermissions?: { [perm: string]: boolean };
  availableRoles?: { [role: string]: boolean };
  roles?: Role[];
  isSupportedOrg?: boolean;
  // Bookkeeping (not in schema but used for internal tracking)
  isSupport?: boolean;

  onboardingParameters?: any;

  static subgroupName(parentOrg: IOrganization, subOrg: IOrganization) {
    return parentOrg.name + ' - ' + subOrg.name;
  }

  static sanitize(org: Partial<IOrganization>) {
    try {
      if (org.orgId) org.orgId = org.orgId.trim();
      org.name = org.name?.trim() ?? '';
      if (org.clientId) org.clientId = org.clientId.trim();
    } catch (err: any) {
      console.error(' Could not sanitize', org, err);
    }
  }
}

export type MyOrgSupportInfo = {
  name: string;
  _id: string;
  orgId;
  isSupport: boolean;
  organizationId: string;
};

export class OrganizationWithRoles extends Organization {
  override roles: Role[];
  extraPaidUsersCount?: number;
}

function fallbackReinvoiceDate(sub) {
  const date = sub.subscriptionReinvoicingDate || sub.startDate || new Date();

  return setToDayBeginning(date);
}

export function getFromAndToPeriods(
  sub: OrderedSubscriptionDetail,
  forceStartFromNow: boolean
): Date[] {
  const startDate = setToDayBeginning(sub.startDate);
  const reinvoiceDate = setToDayBeginning(sub.subscriptionReinvoicingDate);
  const currentMoment = setToDayBeginning(new Date());

  if (currentMoment < startDate || currentMoment < reinvoiceDate) {
    if (forceStartFromNow && currentMoment > startDate) {
      return [currentMoment, reinvoiceDate];
    } else {
      return [startDate, reinvoiceDate];
    }
  } else {
    // This is when current date is after startDate and subscriptionReinvoicingDate
    // Example: new rolling period started. first invoice issued on 2022, second in 2024 (as subscriptionReinvoicingDate)
    // and we are in 2024 now
    let startDate = momentTimezone(fallbackReinvoiceDate(sub));
    let endDate = addPeriod(startDate, sub.isAnnualPrice);
    while (endDate < momentTimezone(currentMoment)) {
      startDate = endDate;
      endDate = addPeriod(startDate, sub.isAnnualPrice);
    }

    if (forceStartFromNow) {
      return [currentMoment, endDate.toDate()];
    } else {
      // We send extra third argument as a hint that we are in reinvoice mode
      return [startDate.toDate(), endDate.toDate(), startDate.toDate()];
    }
  }
}

export function setToDayBeginning(date) {
  const osloTime = momentTimezone(date).tz('Europe/Oslo');
  osloTime.set({ hour: 2, minute: 0, second: 0, millisecond: 0 });
  return osloTime.toDate();
}

function addPeriod(momentDate, isAnnualPrice) {
  const clone = momentDate.clone();
  if (isAnnualPrice) {
    clone.add(1, 'years');
  } else {
    clone.add(1, 'months');
  }
  return clone;
}

export class BisnodeData {
  orgNumber?: string;
  name: any;
  members: any[] = [];
  membersBoard: any[] = [];
  shareholders?: any[];
  address: any;
  postnr: any;
  city: any;
  established: any;
  type: any;
  website: any;
  phone: any;
  accountantName?: string;
  auditorName?: string;
  companyIndustry?: string;
  orgFormLong?: string;
}

export enum SortActionPointOptions {
  DATE = 'DATE',
  TITLE = 'TITLE',
  RESPONSIBLE = 'RESPONSIBLE',
  MEETING_RELATED = 'MEETING_RELATED',
  DEADLINE = 'DEADLINE',
}

export enum TaskStatus {
  TODO = 'TODO',
  DOING = 'DOING',
  DONE = 'DONE',
}

export interface Task {
  _id?: string;
  organization: string;
  finished: boolean;
  title?: string;
  completed?: boolean;
  //ids that this task depends on (stored field)
  taskDependsOn?: string[];
  //The tasks that depend on this (array of IDs or populated Task objects via virtual populate)
  dependentTasks?: string[] | Task[];
  //The task that this is a subtask of
  parentTask?: string;
  //subtasks of this task (array of IDs or populated Task objects via virtual populate)
  subTasks?: string[] | Task[];
  seenUserIdsMap?: { [userId: string]: boolean };
  privateTask?: boolean;
  completionProgress?: number;
  taskStatus?: string;
  taskPriority?: TaskPriority;
  sendEmailToResponsible?: boolean;
  meeting?: string;
  showInMinutesPdf?: boolean;
  category?: AgendaItemCategory;
  agendaItem?: string;
  formControlForMessageInEmail?: boolean;
  notifiedUserIds?: string[];
  iHaveSeenThisTask?: boolean;
  description?: string;
  responsible?: string;
  extraResponsible?: string[];
  date?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  startTime?: Date;
  createdBy?: string;
  dueDiligenceDirentId?: string;
  dueDiligenceDirentPath?: string[];
  showInCalendar?: boolean;
  showInCalendarForEverybody?: boolean;
  // Has to be removed before saving and editing
  isFollowUp?: boolean;
  progress?: number;
  resp?: string;
  labels?: string[];
  //For table only
  labelsArr?: any[];
  updatedBy?: string;

  // Dont use comments from the task, it is here for some reason
  comments?: any;

  // Var for auto repeat tasks not used in frontend
  repeatAnnually?: boolean;
  hasBeenProcessedForAutoRepeat?: boolean;

  __v?: number;
}

export const TaskPriorities = {
  CRITICAL: 'CRITICAL',
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW',
  MAYBE: 'MAYBE',
} as const;

export type TaskPriority = (typeof TaskPriorities)[keyof typeof TaskPriorities];

export const taskPriorityToColor: Record<TaskPriority, string> = {
  CRITICAL: '#B22F2F !important',
  HIGH: '#C73C11 !important',
  MEDIUM: '#F57C00 !important',
  LOW: '#F6BE4D !important',
  MAYBE: 'grey !important',
} as const;

export const taskPriorityToNumber: Record<TaskPriority, number> = {
  CRITICAL: 4,
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
  MAYBE: 0,
} as const;

export interface FollowupItem extends Task {
  followup?: boolean;
  startDate?: Date;
}

export type ActionPoint = Task;

export class Note {
  id?: string;
  noteType: MeetingNoteType;
  text: string;
  createdBy: string;
  createdAt: string;
  constructor(createdBy = '') {
    this.noteType = MeetingNoteType.PRIVATE_NOTE;
    this.text = '';
    this.createdBy = createdBy;
    this.createdAt = new Date().toISOString();
  }
}

export class AgendaItemComment {
  id?: string;
  text: string;
  createdBy: string;
  createdAt: string;
  isPrivateAgendaComment: boolean;

  constructor(createdBy = '') {
    this.text = '';
    this.createdBy = createdBy;
    this.createdAt = new Date().toISOString();
    this.isPrivateAgendaComment = true;
  }
}

export class ActionPointComment {
  _id?: string;
  id?: string;
  text: string;
  createdBy: string;
  createdAt: string;
  formControlForMessageInEmail: boolean;
  sendToAllCheckbox: boolean;
  notifiedUserIds: string[];
  constructor(createdBy = '') {
    this.text = '';
    this.createdBy = createdBy;
    this.createdAt = new Date().toISOString();
    this.formControlForMessageInEmail = false;
    this.sendToAllCheckbox = false;
    this.notifiedUserIds = [];
  }
}

export class AgendaItemVoting {
  votingFor: string[] = [];
  votingAgainst: string[] = [];
  votingNeutral: string[] = [];
}

export class AgendaItemElection {
  votingFor: string[] = [];
  value = '';
  userId?: string;
}

export interface NewCandidate {
  name: string;
  userId?: string;
}

export interface GQLAgendaItem {
  id: string;
  title: string;
  description: string;
  agendaItemCategory: AgendaItemCategory;
  duration?: number;
  agendaStartingIndex?: number;
  agendaResponsible?: string;
  agendaItemIsPrivate?: boolean;
  agendaItemOpenSpeakingList?: boolean;
  agendaItemIsDraft?: boolean;
  agendaItemHasCommentsEnabled?: boolean;
  hideAgendaNumbering?: boolean;
  agendaItemSuggestionBasisId?: string;
  proposedResolution?: string;
  decisionVotes?: DecisionVote[];
  electionVotes?: ElectionVote[];
  electionCandidates?: ElectionCandidate[];
  nCandidatesToElect?: number;
  isWinnerSigningPdf?: boolean;
  voting?: AgendaItemVoting;
  electionNCandidatesToElect?: number;
  election?: AgendaItemElection[];
  isVotingLocked?: boolean;
  gaMajorityRequirementRule?: GAMajorityRequirementRule;
  agendaItemSuggestionBasis: any;
  iHaveSeenThisAgendaItem?: string[];
  minuteNotes: string;
  decisionNotes: string;
  drawResolutionVote?: boolean;
  agendaItemAttachedFiles: string[];
  agendaItemAttachments: AgendaItemAttachment[];
  accumulatedDuration?: number;
  parsedDurationText?: string;
  restrictedVoters?: string[];
  allowedVoters?: string[];
  numberOfComments?: number;
}

export interface NewsItemAttachment {
  name: string;
  fileSize: number;
  direntId: string;
}
export interface NewsItem {
  _id: string;
  organizationId: string;
  isDraft: boolean;
  userId: string;
  title: string;
  description: string;
  newsFileAttachments?: NewsItemAttachment[];
  newsTopics: string[];
  notifiedUserIds: string[];
  parentIdForFiles: string;
  isOnlyForBoard: boolean;
  createdAt: string;
  updatedAt: string;
  labels: string[];

  formControlForMessageInEmail?: boolean;
  formControlForAttachmentInEmail?: boolean;
  formControlCheckboxBoard?: boolean;
  formControlCheckboxShareholders?: boolean;
  formControlCheckboxAll?: boolean;
}
export interface MessageItem {
  _id: string;
  recipients: string[];
  recipientIds: string[];
  mesageType: string;
  subject: string;
  generalid?: string;
  userId: string;
  organizationId: string; //  organization context
  meetingId?: string; // meeting context
  direntId?: string; //  for file operations.
  signOrderId?: string; // sign orders  signOrderSchema.orderId
  requestId?: string; // signicat signingId
  taskId?: string; // indicidual taskId
  generalSendtMessage?: string;
  createdAt: string;
}
export enum MessageType {
  // IN THE LIBRARY THAT IT FITS IN
  MEETING_INVITE = 'MEETING_INVITE',
}
export interface Emailreport {
  _id: string;
  payload: string[];
  messageId: string;
  orgId: string;
  meetingId?: string;
  status: string;
  email?: string;
}

export class AnnualPlanConfiguration {
  // Not used at the moment because we don't target org level yet
  // organization?: string;
  orgTypes: string[];
  published: boolean;
  locale: string;
  configName: string;
  // It is array of arrays
  configurations: [[{ monthIndex: number; agendaItemSuggestionIds: string[] }]];
}

export class SurveyItem {
  _id: string;
  sectionName: string;
  questionText: string;
  answerOptions: string[];
  maxAnswers: number;
  helpText: string;
  allowFreeForm?: boolean;
  allowExtraFreeForm?: boolean;
}

export class SurveyRunningType {
  _id: MongoId;
  title: string;
  organization: string;
  language: OrgLanguage;
  generalComments: string;
  surveyItems: SurveyItem[];
  invitedUsers: string[];
  modifiedBy: string;
  isCustomSurvey?: boolean;
  createdAt: string;
  isPublic?: boolean;
  submittedSurveys: any[];
}

export class SurveyConfiguration {
  _id: string;
  organization?: string;
  orgTypes: string[];
  published: boolean;
  language: OrgLanguage;
  configName: string;
  modifiedBy: string;
  surveyItems: SurveyItem[];
  description: string;
  updatedAt: string;
  createdAt: string;
  isPublic?: boolean;
}

export class GlobalNewsTemplateStep {
  stepName: string;
  value: string;
}

export class GlobalNewsConfiguration {
  _id: string;
  published: boolean;
  locale: string;
  configName: string;
  modifiedBy: string;
  newsTemplateSteps: GlobalNewsTemplateStep[];
  createdAt: Date;
  updatedAt: Date;
}

export type TemplateTagConfigType = {
  tagName: string;
  tagDataType: TemplateTagType;
  tagDefaultValue?: string;
  tagHintTextValue?: string;
  placeholder: string;
  tagOptionValues: string[];
  required?: boolean;
  spacingValueSett?: string;
};

export class LegalTemplateConfiguration {
  _id: string;
  published: boolean;
  language: OrgLanguage;
  configName: string;
  modifiedBy: string;
  organization?: string;
  orgTypes?: string[];
  templateBody: string;
  description: string;
  templateTags: TemplateTagConfigType[];
  defaultFolderPath?: string;
  sortOrderIndex?: number;
}
export enum ORGBRAIN_ACADEMY_TYPES {
  GUIDE = 'GUIDE',
  WEBINAR = 'WEBINAR',
  TRAINING = 'TRAINING',
  NEWS = 'NEWS',
}

export class AcademyConfig {
  _id: string;
  language: OrgLanguage;
  tileImageUrl: string;
  title: string;
  resourceUrl: string;
  sortOrderIndex: number;
  published: boolean;
  hideTextUnderImage: boolean;
  hidePlayIconOnImage: boolean;
  modifiedBy: string;
}

export class SavedLegalTemplate {
  _id: string;
  organization: string;
  templateId: string;
  htmlValue: string;
  tagsValueJSON: string;
  direntIdAsString: string;
  sharedFolderOwnerOrganizationId: string;
  configName: string;
  language: string;
  modifiedBy: string;
}

export enum GlobalNewsTemplateSteps {
  STEP_HEADER_TEXT = 'STEP_HEADER_TEXT',
  STEP_IMAGE_URL = 'STEP_IMAGE_URL',
  STEP_DESCRIPTION = 'STEP_DESCRIPTION',
  EMPTY_SPACE = 'EMPTY_SPACE',
}

export class MinutesApprovalsType {
  id: string;
  direntId: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

export class SpeakingList {
  id: string;
  organizationId: string;
  meetingId: string;
  agendaItemId: string;
  hasFinishedSpeech: boolean;
  isParticipatingInSpeakingList: boolean;
  orderIndex: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export class AgendaItemLog {
  _id: string;
  organizationId: string;
  meetingId: string;
  agendaItemId: string;
  field: string;
  oldValue: string;
  newValue: string;
  agendaItemName: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface GQLMeeting {
  _id?: string;
  id: string;
  title?: string;
  number: number;
  numberTitle?: string;
  fullMeetingTitle?: string;
  startDate: string;
  endDate: string;
  organization?: string;
  meetingType: MeetingType;
  procedureType: ProcedureType;
  status: BOARDMEETING_STATUS;
  minutesStatus?: MINUTES_STEP_STATUS;
  locationName?: string;
  videoConfUrl?: string;
  preVotingEnabled?: boolean;
  filesSectionHidden?: boolean;
  hideVideoIcon?: boolean;
  hideChatIcon?: boolean;
  hideProxyForm?: boolean;
  quorum?: boolean;
  meetingLeader?: string;
  practicalInformation: string;
  isPartOfAnnualPlan: boolean;
  hasUnseenChatMessagesForUser?: number;
  nUnseenChatMessagesForUser?: number;
  hasUnreadEmailsForUser: number;
  agendaItems: GQLAgendaItem[];
  agendaGeneralComments?: string;
  minutesGeneralComments?: string;
  minutesApprovals?: MinutesApprovalsType[];
  notes: Note[];
  iHaveSeenTheWholeMeeting?: string[];
  isClosedForMoreParticipants?: boolean;
  minutesGroupActiveConsolidated?: boolean;
  minutesGroupActiveSeparate?: boolean;
  minutesGroupReference?: string;
}

export interface MeetingQueryResponse {
  meeting: GQLMeeting;
}
export interface GQLMeetingEmail {
  id: string;
  meetingId: string;
  organizationId: string;
  isReadByUser: boolean;
  envelopeFromAddress: string;
  fromNamesAndAddresses: { name?: string; address?: string }[];
  timeReceived: string;
  subject: string;
  bodyPlainText: string;
  bodyHtml: string;
  attachments: { direntId: string; fileName: string }[];
  attachmentsFolderName?: string;
}

export enum BillingPackagePermission {
  READ = 'READ',
  ORDER = 'ORDER',
  MODIFY = 'MODIFY',
}

export {
  BillingPackageID,
  BillingProductType,
  DiscountItem,
  DiscountType,
  ExtraPaidService,
  OrderedSubscriptionDetail,
  OrganizationSubscription,
  PaidExtraService,
  ProductSchema,
  ReinvoicingRadioButtonType,
};

export interface OrganizationSubscriptionDeclaration {
  packageId: BillingPackageID;
  packageName: string;
  orderIndex: number;
  packagePrices: PackagePrice[];
  subgroupUserPrices: PackagePrice[];
  userPrices: PackagePrice[];
  ddProcessPrices: PackagePrice[];
  signaturePrices: PackagePrice[];
  storagePrices: PackagePrice[];
  packageMaxValidityDays: number;
  isMainSubscription: boolean;
  includedStorage?: number;
  includedBankIdSignatures?: Number;
  includedUsers: number;
  isPackageHiddon?: boolean;
  onlyClientAdminCanOrder?: boolean;
  packageBuilder?: boolean;
  packageBuilderNIF?: boolean; // a hack to hide Cancel button in a NIF mode
  onlyClientAdminCanSeeIt?: boolean;
  hidePriceSection?: boolean;
  hideAnnualSelector?: boolean;
  autoRenewAfterQuotaIsOver?: boolean;
  quotaRenewalTriggers?: string[];
  reinvoicingRadioButton: ReinvoicingRadioButtonType;
  discounts?: DiscountItem[];
  includeOrgTypes?: CompanyAcronymsEnum[];
  excludeOrgTypes?: CompanyAcronymsEnum[];
  category: PACKAGE_CATEGORY;
  pricePromoText?: any;
  featured?: boolean;
  icon: string;
  texts1?: string[];
  features1?: string[];
  texts1en?: string[];
  features1en?: string[];
  texts1nb?: string[];
  features1nb?: string[];
  customPricesTexts?: string;
  // If ordered this package, all auxiliary subscriptions are dropped
  triggersDropOfAuxiliarySubscriptions?: boolean;
  // This package is dropped when a main package is ordered (if main has triggersDropOfAuxiliarySubscriptions: true)
  dropThisOnOrderMainPackage?: boolean;
}

export enum InvoiceActionType {
  INVOICE_DELETE = 'INVOICE_DELETE',
  SEND_AS_ORDER = 'SEND_AS_ORDER',
  SEND_AS_INVOICE = 'SEND_AS_INVOICE',
  INVOICE_ID_CONNECTED = 'INVOICE_ID_CONNECTED',
}

export interface Invoice {
  _id?: string;
  organization: string;
  tripletexInvoiceId?: string;
  tripletexOrderId?: string;
  preliminaryInvoiceId?: string;
  tripletexClientId: string;
  requesterId: string;
  invoicePaid: boolean;
  runningSubscriptionForInvoice: string;
  subscriptionEnumKey: BillingPackageID;
  totalPrice: number;
  paymentDue: Date;
  paymentPeriodFrom: Date;
  paymentPeriodTo: Date;
  isAnnual: boolean;
  products: ProductSchema[];
  errorsList?: string[];
  generalInvoiceComment?: string;
  internalInvoiceComment?: string;
  reference?: string;
  organizationName: string;
  orgId: string;
  currency: string;
  modificationLog: { userId?: string; actionType: InvoiceActionType; anyData?: any }[];
}

export type UserCountMapType = {
  mainOrg: {
    [key: string]: number;
  };
  subOrg: { [key: string]: number };
  totalUniqueUsers: number;
  uniqeUsersInSubgroups: number;
  totalUniqueUsersExlShareholders: number;
  mainOrgUniqueUsersExlShareholders: number;
  uniqueUsersShareholders: number;
};

export function makeReinvoicingDate(origDate, isAnnualPrice, moment) {
  let dateMidnightLocal = moment(origDate);
  if (isAnnualPrice) {
    dateMidnightLocal.add(1, 'years');
  } else {
    dateMidnightLocal.add(1, 'months');
  }

  return dateMidnightLocal.toDate();
}

export function filterDiscountsByTimestamp(discounts: DiscountItem[], moment) {
  discounts = discounts || [];
  return discounts.filter((item) => {
    if (!item.discountAddedTimestamp) {
      return false;
    }
    if (item.discountDurationMonths < 0) {
      return true;
    }
    if (item.discountDurationMonths % 12 === 0) {
      return (
        new Date() <
        moment(item.discountAddedTimestamp)
          .add(Math.round(item.discountDurationMonths / 12), 'years')
          .toDate()
      );
    } else {
      return (
        new Date() <=
        moment(item.discountAddedTimestamp).add(item.discountDurationMonths, 'months').toDate()
      );
    }
  });
}

export function helpApplyPriceDiscounts({
  priceBeforeDiscount,
  discounts,
  moment,
}: {
  priceBeforeDiscount: number;
  discounts: DiscountItem[];
  moment: any;
}): {
  percentDiscountMap: { [key: string]: number };
  finalPrice: number;
  sortedPriceDiscounts: DiscountItem[];
  totalDiscountsAmount: number;
} {
  priceBeforeDiscount = Math.max(priceBeforeDiscount || 0, 0);
  let result = {
    percentDiscountMap: {},
    finalPrice: priceBeforeDiscount,
    sortedPriceDiscounts: [],
    totalDiscountsAmount: 0,
  };

  const sortedDiscounts = filterDiscountsByTimestamp(discounts, moment).sort((a, b) => {
    if (a.discountType === b.discountType) {
      return 0;
    }

    if (a.discountType !== DiscountType.DISCOUNT_PERCENT_TOTAL) {
      return -1;
    } else {
      return 1;
    }
  });

  for (const discount of sortedDiscounts) {
    if (discount.discountType === DiscountType.DISCOUNT_CASH_TOTAL) {
      result.sortedPriceDiscounts.push(discount);
      result.finalPrice = Math.max(precisionHelper(result.finalPrice - discount.discountAmount), 0);
      discount.dicountAmountCalculated = discount.discountAmount;
      result.totalDiscountsAmount += discount.dicountAmountCalculated;
    }

    if (discount.discountType === DiscountType.DISCOUNT_PERCENT_TOTAL) {
      result.sortedPriceDiscounts.push(discount);
      const dicountAmountCalculated = precisionHelper(
        (discount.discountAmount * result.finalPrice) / 100
      );
      result.percentDiscountMap[
        `${DiscountType.DISCOUNT_PERCENT_TOTAL}${discount.discountAmount}`
      ] = dicountAmountCalculated;
      result.finalPrice = Math.max(precisionHelper(result.finalPrice - dicountAmountCalculated), 0);
      discount.dicountAmountCalculated = dicountAmountCalculated;
      result.totalDiscountsAmount += discount.dicountAmountCalculated;
    }
  }

  return result;
}

export function helpApplyUserDiscounts({
  discounts,
  extraPaidUsersCount,
  extraPaidSubgrUsersCount,
  userCountMap,
  freeUsers,
  moment,
}: {
  discounts: DiscountItem[];
  freeUsers: number;
  extraPaidUsersCount: number;
  extraPaidSubgrUsersCount: number;
  userCountMap: UserCountMapType;
  moment: any;
}): { payableUsers: number; payableSubgroupUsers: number } {
  const result = {
    payableUsers: 0,
    payableSubgroupUsers: 0,
  };
  if (freeUsers < 0) {
    freeUsers = 0;
  }
  if (!extraPaidUsersCount || extraPaidUsersCount < 0) {
    extraPaidUsersCount = 0;
  }
  if (!extraPaidSubgrUsersCount || extraPaidSubgrUsersCount < 0) {
    extraPaidSubgrUsersCount = 0;
  }
  userCountMap = structuredClone(userCountMap);
  discounts = filterDiscountsByTimestamp(discounts, moment);

  // Subtract free users
  result.payableUsers = userCountMap.mainOrgUniqueUsersExlShareholders - freeUsers;
  if (result.payableUsers < 0) {
    freeUsers = Math.abs(result.payableUsers);
    result.payableUsers = 0;
  } else {
    freeUsers = 0;
  }
  result.payableSubgroupUsers = userCountMap.uniqeUsersInSubgroups - freeUsers;
  // Subtract free users end

  // Subtract paid users
  result.payableUsers = result.payableUsers - extraPaidUsersCount;
  if (result.payableUsers < 0) {
    extraPaidUsersCount = Math.abs(result.payableUsers);
    result.payableUsers = 0;
  } else {
    extraPaidUsersCount = 0;
  }
  result.payableSubgroupUsers =
    result.payableSubgroupUsers - extraPaidUsersCount - extraPaidSubgrUsersCount;
  if (result.payableSubgroupUsers < 0) {
    result.payableSubgroupUsers = 0;
  }
  // Subtract paid users end

  for (const discount of discounts) {
    if (discount.discountType === DiscountType.DISCOUNT_USER_COUNT_SUBGROUP) {
      const discountAmount = Math.max(discount.discountAmount, 0);
      result.payableSubgroupUsers = Math.max(result.payableSubgroupUsers - discountAmount, 0);
    }

    if (discount.discountType === DiscountType.DISCOUNT_USER_COUNT_GENERAL) {
      let discountAmount = Math.max(discount.discountAmount, 0);
      result.payableUsers = result.payableUsers - discountAmount;
      if (result.payableUsers < 0) {
        discountAmount = Math.abs(result.payableUsers);
        result.payableUsers = 0;
      } else {
        discountAmount = 0;
      }
      result.payableSubgroupUsers = Math.max(result.payableSubgroupUsers - discountAmount, 0);
    }
  }

  applyPerRoleUserDiscount({
    intermediateResult: result,
    discounts,
    userCountMap,
  });

  return result;
}

function precisionHelper(value) {
  return roundToPrecision(value, 2);
}

export function isForbiddenToChangeStartDateOfSubscription({
  tripletexClientId,
  orderingPackageId,
  currentPackageId,
  startDateOfCurrentPackage,
}: {
  tripletexClientId: string;
  orderingPackageId: BillingPackageID;
  currentPackageId: BillingPackageID;
  startDateOfCurrentPackage: Date;
}) {
  return (
    tripletexClientId &&
    currentPackageId === orderingPackageId &&
    startDateOfCurrentPackage &&
    new Date(startDateOfCurrentPackage).getTime() <= Date.now()
  );
}

export function calculateSubscriptionPricingDetails({
  selectedPackage,
  moment,
  subscriptionDetail,
  userCountMap,
}: {
  selectedPackage: OrganizationSubscriptionDeclaration;
  moment: any;
  subscriptionDetail: OrderedSubscriptionDetail;
  userCountMap: UserCountMapType;
}): {
  packagePrice: number;
  payableUsers: number;
  payableUsersMoneySubtotal: number;
  payableSubgroupUsersMoneySubtotal: number;
  totalPriceForDDProcesses: number;
  extraUsersMoneySubtotal: number;
  extraSubgroupUsersMoneySubtotal: number;
  totalMoneySubtotal: number;
  percentDiscountMap: { [key: string]: number };
  userPrice: number;
  ddProcessPrice: number;
  subgrUserPrice: number;
  payableSubgroupUsers: number;
  sortedPriceDiscounts: DiscountItem[];
  totalDiscountsAmount: number;
} {
  const result = helpApplyUserDiscounts({
    discounts: subscriptionDetail.discounts,
    extraPaidUsersCount: subscriptionDetail.userCount,
    extraPaidSubgrUsersCount: subscriptionDetail.userSubgrCount,
    userCountMap: userCountMap,
    freeUsers: selectedPackage.includedUsers || 0,
    moment,
  });

  const payableUsers = result.payableUsers;
  const payableSubgroupUsers = result.payableSubgroupUsers;

  const {
    totalDiscountsAmount,
    payableUsersMoneySubtotal,
    payableSubgroupUsersMoneySubtotal,
    totalPriceForDDProcesses,
    extraUsersMoneySubtotal,
    extraSubgroupUsersMoneySubtotal,
    totalMoneySubtotal,
    percentDiscountMap,
    userPrice,
    ddProcessPrice,
    subgrUserPrice,
    sortedPriceDiscounts,
    packagePrice,
  } = finalStepPriceResultHelper({
    selectedPackage,
    payableUsers,
    payableSubgroupUsers,
    subscriptionDetail,
    moment,
  });

  return {
    totalDiscountsAmount,
    packagePrice,
    payableUsers,
    payableUsersMoneySubtotal,
    payableSubgroupUsersMoneySubtotal,
    totalPriceForDDProcesses,
    extraUsersMoneySubtotal,
    extraSubgroupUsersMoneySubtotal,
    totalMoneySubtotal,
    percentDiscountMap,
    userPrice,
    ddProcessPrice,
    subgrUserPrice,
    payableSubgroupUsers,
    sortedPriceDiscounts,
  };
}

export function finalStepPriceResultHelper({
  selectedPackage,
  moment,
  payableUsers,
  payableSubgroupUsers,
  subscriptionDetail,
}: {
  selectedPackage: OrganizationSubscriptionDeclaration;
  moment: any;
  payableUsers: number;
  payableSubgroupUsers: number;
  subscriptionDetail: OrderedSubscriptionDetail;
}): {
  totalDiscountsAmount: number;
  payableUsersMoneySubtotal: number;
  payableSubgroupUsersMoneySubtotal: number;
  totalPriceForDDProcesses: number;
  extraUsersMoneySubtotal: number;
  extraSubgroupUsersMoneySubtotal: number;
  totalMoneySubtotal: number;
  percentDiscountMap: { [key: string]: number };
  userPrice: number;
  ddProcessPrice: number;
  subgrUserPrice: number;
  sortedPriceDiscounts: DiscountItem[];
  packagePrice: number;
} {
  const isAnnualPrice = subscriptionDetail.isAnnualPrice;

  let packagePrice = getPriceFromSubscriptionHelper({
    subscriptionDetail,
    priceKey: 'packagePrices',
    validity: new Date(),
    subscriptionEnumKey: selectedPackage.packageId,
  });
  if (
    selectedPackage.packageId === BillingPackageID.ENTERPRISE &&
    subscriptionDetail.customPackagePrice >= 0
  ) {
    packagePrice = subscriptionDetail.customPackagePrice;
  }

  const userPrice = getPriceFromSubscriptionHelper({
    subscriptionDetail,
    priceKey: 'userPrices',
    validity: new Date(),
    subscriptionEnumKey: selectedPackage.packageId,
  });

  const subgrUserPrice = getPriceFromSubscriptionHelper({
    subscriptionDetail,
    priceKey: 'subgroupUserPrices',
    validity: new Date(),
    subscriptionEnumKey: selectedPackage.packageId,
  });

  const payableUsersMoneySubtotal = precisionHelper(payableUsers * userPrice);
  const payableSubgroupUsersMoneySubtotal = precisionHelper(payableSubgroupUsers * subgrUserPrice);

  const ddProcessPrice =
    selectedPackage.ddProcessPrices?.find((p) => p.isAnnualPrice === isAnnualPrice)?.price || 0;

  const totalPriceForDDProcesses = precisionHelper(
    ddProcessPrice * (subscriptionDetail.ddProcessesCount || 0)
  );

  const extraUsersMoneySubtotal = precisionHelper(
    Math.max(subscriptionDetail.userCount || 0, 0) * userPrice
  );
  const extraSubgroupUsersMoneySubtotal = precisionHelper(
    Math.max(subscriptionDetail.userSubgrCount || 0, 0) * subgrUserPrice
  );

  const resultPriceDisounts = helpApplyPriceDiscounts({
    priceBeforeDiscount:
      packagePrice +
      extraUsersMoneySubtotal +
      extraSubgroupUsersMoneySubtotal +
      payableUsersMoneySubtotal +
      payableSubgroupUsersMoneySubtotal +
      totalPriceForDDProcesses,
    discounts: subscriptionDetail.discounts,
    moment,
  });
  const totalDiscountsAmount = precisionHelper(resultPriceDisounts.totalDiscountsAmount);
  const percentDiscountMap = resultPriceDisounts.percentDiscountMap;
  const totalMoneySubtotal = resultPriceDisounts.finalPrice;

  return {
    packagePrice,
    totalDiscountsAmount,
    payableUsersMoneySubtotal,
    payableSubgroupUsersMoneySubtotal,
    totalPriceForDDProcesses,
    extraUsersMoneySubtotal,
    extraSubgroupUsersMoneySubtotal,
    totalMoneySubtotal,
    percentDiscountMap,
    userPrice,
    ddProcessPrice,
    subgrUserPrice,
    sortedPriceDiscounts: resultPriceDisounts.sortedPriceDiscounts,
  };
}

export async function assignNewPricesAndValidity({
  validity,
  subscription,
  draft,
}: {
  validity: Date;
  subscription: OrderedSubscriptionDetail;
  draft: OrganizationSubscriptionDeclaration;
}) {
  const uPrice = draft.userPrices?.find(
    (item) => item.isAnnualPrice === subscription.isAnnualPrice
  );
  const suPrice = draft.subgroupUserPrices?.find(
    (item) => item.isAnnualPrice === subscription.isAnnualPrice
  );
  const pPrice = draft.packagePrices?.find(
    (item) => item.isAnnualPrice === subscription.isAnnualPrice
  );
  if (uPrice) {
    subscription.userPrices = subscription.userPrices || [];

    if (
      !subscription.userPrices.find((price) => {
        return (
          price.price === uPrice.price &&
          price.isAnnualPrice === uPrice.isAnnualPrice &&
          price.currency === uPrice.currency &&
          new Date(price.validUntil).getTime() === new Date(validity).getTime()
        );
      })
    ) {
      subscription.userPrices.push({ ...uPrice, validUntil: validity });
    }
  }

  if (suPrice) {
    subscription.subgroupUserPrices = subscription.subgroupUserPrices || [];

    if (
      !subscription.subgroupUserPrices.find((price) => {
        return (
          price.price === suPrice.price &&
          price.isAnnualPrice === suPrice.isAnnualPrice &&
          price.currency === suPrice.currency &&
          new Date(price.validUntil).getTime() === new Date(validity).getTime()
        );
      })
    ) {
      subscription.subgroupUserPrices.push({ ...suPrice, validUntil: validity });
    }
  }

  if (pPrice) {
    subscription.packagePrices = subscription.packagePrices || [];

    if (
      !subscription.packagePrices.find((price) => {
        return (
          price.price === pPrice.price &&
          price.isAnnualPrice === pPrice.isAnnualPrice &&
          price.currency === pPrice.currency &&
          new Date(price.validUntil).getTime() === new Date(validity).getTime()
        );
      })
    ) {
      subscription.packagePrices.push({ ...pPrice, validUntil: validity });
    }
  }
}

export function getPriceFromSubscriptionHelper({
  subscriptionDetail,
  priceKey,
  validity,
  subscriptionEnumKey,
}: {
  subscriptionDetail: OrderedSubscriptionDetail;
  priceKey: string;
  validity: Date;
  subscriptionEnumKey: BillingPackageID;
}) {
  const priceLocalPackage = (subscriptionDetail[priceKey] || []).find((item) => {
    return new Date(item.validUntil) > new Date(validity);
  });
  if (typeof priceLocalPackage?.price === 'number') {
    return priceLocalPackage.price;
  } else {
    const pricePack = (packageMap[subscriptionEnumKey][priceKey] || []).find(
      (item) => item.isAnnualPrice === subscriptionDetail.isAnnualPrice
    );
    return pricePack?.price || 0;
  }
}
function getCombinations(roles) {
  let result = [];

  function helper(start, combo) {
    for (let i = start; i < roles.length; i++) {
      const copy = [...combo];
      copy.push(roles[i]);
      result.push(copy);
      helper(i + 1, copy);
    }
  }
  helper(0, []);
  return result;
}

function applyPerRoleUserDiscount({
  intermediateResult,
  discounts,
  userCountMap,
}: {
  intermediateResult: {
    payableUsers: number;
    payableSubgroupUsers: number;
  };
  discounts: DiscountItem[];
  userCountMap: UserCountMapType;
}) {
  if (intermediateResult.payableSubgroupUsers === 0 && intermediateResult.payableUsers === 0) {
    return;
  }

  const discountMap: { [key: string]: number } = {};
  for (const discount of discounts) {
    if (discount.discountType === DiscountType.DISCOUNT_USER_COUNT_ROLE_BASED) {
      let useAmount = discount.discountAmount;
      if (useAmount === -1) {
        useAmount = 9999999999;
      }
      let discountAmount = Math.max(useAmount, 0);
      discountMap[discount.discountRole] = discountMap[discount.discountRole] || 0;
      discountMap[discount.discountRole] += discountAmount;
    }
  }

  const discountKeys = Object.keys(discountMap);
  discountKeys.sort((a, b) => rolesPriorityMap[a] - rolesPriorityMap[b])[0];

  const combinations = getCombinations(discountKeys);
  combinations.sort((a, b) => a.length - b.length);
  const levels = [];
  for (let i = 1; i <= discountKeys.length; i++) {
    let level = [];
    for (const loopItem of combinations) {
      if (loopItem.length === i) {
        level.push(loopItem);
      }
    }
    levels.push(level);
  }

  for (const level of levels) {
    helpDeductDescountByRoleFinal({
      level,
      intermediateResult,
      discountMap,
      userCountMap,
      keyForIntermediateResult: 'payableUsers',
      keyForUserCountMap: 'mainOrg',
    });

    helpDeductDescountByRoleFinal({
      level,
      intermediateResult,
      discountMap,
      userCountMap,
      keyForIntermediateResult: 'payableSubgroupUsers',
      keyForUserCountMap: 'subOrg',
    });
  }
}

function helpDeductDescountByRoleFinal({
  level,
  intermediateResult,
  discountMap,
  userCountMap,
  keyForIntermediateResult,
  keyForUserCountMap,
}) {
  for (const roleArray of level) {
    if (!intermediateResult[keyForIntermediateResult]) {
      return;
    }

    const joinedRoles = roleArray.join(',');
    const deductibleArray: number[] = [];
    for (const role of roleArray) {
      deductibleArray.push(Math.max(discountMap[role], 0));
    }
    let minCommonDeductible = Math.min(...deductibleArray);

    const countUsersWithRole = userCountMap[keyForUserCountMap][joinedRoles];
    if (
      minCommonDeductible &&
      countUsersWithRole > 0 &&
      intermediateResult[keyForIntermediateResult] > 0
    ) {
      const deductAmount = Math.min(
        minCommonDeductible,
        countUsersWithRole,
        intermediateResult[keyForIntermediateResult]
      );

      intermediateResult[keyForIntermediateResult] =
        intermediateResult[keyForIntermediateResult] - deductAmount;
      deductRemainingDiscount({
        deductedDiff: deductAmount,
        roleArray,
        discountMap,
      });
    }
  }
}

export function listExtraVariableCosts({
  organizationSubscription,
  paidServices,
}: {
  organizationSubscription: OrganizationSubscription;
  paidServices: PaidExtraService[];
}) {
  const loopData = paidServices || [];

  const newLoop = [];
  let freeCountSignatures = organizationSubscription.includedSignaturesPerYear || 0;
  for (const item of loopData) {
    let pricePerItem = 0;
    const paidServiceType = item.paidServiceType || (item as any).provider;
    if (paidServiceType === ExtraPaidService.SIGNATURE_CRIIPTO) {
      if (freeCountSignatures > 0) {
        freeCountSignatures--;
      } else {
        pricePerItem = getDefaultPriceHelper(paidServiceType, organizationSubscription);
      }
    } else {
      pricePerItem = getDefaultPriceHelper(paidServiceType, organizationSubscription);
    }

    if (!pricePerItem || pricePerItem <= 0) {
      continue;
    }
    const loopDraftItem = {
      _id: item._id,
      userId: item.userId,
      paidServiceType,
      pricePerItem,
      quantity: item.quantity || 1,
      createdAt: item.createdAt,
    };

    newLoop.push(loopDraftItem);
  }

  return newLoop;
}

function getDefaultPriceHelper(paidServiceType: string, data: OrganizationSubscription) {
  switch (paidServiceType) {
    case CredentialEnum.SMS:
      return data.pricePerSmsIndetification;
    case CredentialEnum.BANKID:
      return data.pricePerBankIdIdentification;
    case ExtraPaidService.STORAGE:
      return data.pricePerGbPerMonth;
    case ExtraPaidService.SIGNATURE_CRIIPTO:
      return data.pricePerExtraSignature;
    default:
      return 0;
  }
}

function deductRemainingDiscount({ deductedDiff, roleArray, discountMap }) {
  if (deductedDiff > 0) {
    for (const role of roleArray) {
      discountMap[role] = Math.max(discountMap[role] - deductedDiff, 0);
    }
  }
}

export interface TripletextClientRecord {
  id: string;
  name: string;
  organizationNumber;
  email: string;
  invoiceEmail: string;
  invoiceSendMethod: string;
  address: string;
}

export type BankidCostRecord = {
  _id?: string;
  organizationId: string;
  userId: string;
  data: any;
  context: string;
  provider: CredentialEnum;
  invoiceCreated?: boolean;
};

export function isFreePackage(sub: OrderedSubscriptionDetail) {
  return [
    BillingPackageID.TEST,
    BillingPackageID.TEST2024,
    BillingPackageID.DEPENDENT,
    BillingPackageID.SUSPENDED,
    BillingPackageID.FREEMIUM,
  ].includes(sub?.subscriptionEnumKey);
}

// This is a simplified version of frontend's this.getOrderedpackageCurrentAndPrev(parentOrg) which is used on orgs list for reporting purpose
export function getActiveSubscriptionsHelper(myPackages: OrderedSubscriptionDetail[]): {
  activeMainSubscription: OrderedSubscriptionDetail | null;
  firstPaidSubscription: OrderedSubscriptionDetail | null;
  previousSubscription: OrderedSubscriptionDetail | null;
  activeSecondarySubscriptions: OrderedSubscriptionDetail[];
  allActiveSubscriptions: OrderedSubscriptionDetail[];
} {
  myPackages = myPackages || [];
  let activeMainSubscription = null;
  let firstPaidSubscription = null;
  let previousSubscription = null;
  const allActiveSubscriptions = [];
  const activeSecondarySubscriptions = [];
  let mainSubHelperVar = null;
  let count = 0;
  const now = Date.now();
  for (const anyPack of myPackages) {
    count++;

    let isLast = count === myPackages.length;
    const p: OrderedSubscriptionDetail = (anyPack as any)?.toJSON
      ? (anyPack as any).toJSON()
      : anyPack;
    let passesValidUntilDateCheck = !p.validUntil || new Date(p.validUntil).getTime() > now;

    // for a legacy reason we allow expired TEST packages. do we want to deprecate it?
    let passFreePack = [BillingPackageID.TEST].includes(p.subscriptionEnumKey);

    firstPaidSubscription =
      firstPaidSubscription || (p.isMainSubscription && !isFreePackage(p) && p);

    if (!previousSubscription && p.isMainSubscription) {
      previousSubscription = p;
    }

    if (p.isMainSubscription) {
      previousSubscription = mainSubHelperVar || previousSubscription;
      mainSubHelperVar = p;
    }

    // isLast && passFreePack is a legacy contruction that is created to make sure that legacy customers of TEST package don't lose access to the portal
    passesValidUntilDateCheck = passesValidUntilDateCheck || (isLast && passFreePack);
    if (passesValidUntilDateCheck) {
      allActiveSubscriptions.push(p);
      if (p.isMainSubscription) {
        activeMainSubscription = p;
      } else {
        activeSecondarySubscriptions.push(p);
      }
    }
  }

  // Sort subscriptions so that main subscription is always first
  allActiveSubscriptions.sort((a, b) => {
    if (a.isMainSubscription && !b.isMainSubscription) {
      return -1;
    } else if (!a.isMainSubscription && b.isMainSubscription) {
      return 1;
    } else {
      return 0;
    }
  });
  return {
    activeMainSubscription,
    activeSecondarySubscriptions,
    allActiveSubscriptions,
    firstPaidSubscription,
    previousSubscription,
  };
}

export function meetingIsDraft(meeting: GQLMeeting) {
  switch (meeting.status) {
    case BOARDMEETING_STATUS.DRAFT:
      return true;
    default:
      return false;
  }
}

export const normalizeOrgLanguageToLocale = (
  lang: OrgLanguage | string,
  forceUnfinishedLanguages = false
) => {
  lang = lang || OrgLanguage.NO;
  const langLower = lang.toLowerCase();
  const map = {
    // https://www.loc.gov/standards/iso639-2/php/code_list.php
    en: 'en',
    no: 'nb',
    // Todo this is used to be sent to backend, and backend does not know Swedish:)
    se: forceUnfinishedLanguages ? 'sv' : 'en', // TODO this should be sv after we add translations to tran.ts (TranslationHelper), locale files and Email templates, Pdf generation scripts
  };
  return map[langLower] || 'en';
};

export function userToBmHelper({
  user,
  locale,
  orgType,
  roleMap,
  roleTranslateTexts,
}: {
  user: User;
  locale: string;
  orgType: CompanyAcronymsEnum;
  roleMap: RoleMapType;
  roleTranslateTexts?: TranslationTextMap;
}): BoardMember {
  const bm = new BoardMember();
  bm.id = user._id?.toString();
  // Don't blame me for this - it's needed to make things more compatible in users/entities list and to Don't Repeat Yourself
  bm._id = user._id?.toString();
  bm.first_name = user.firstName;
  bm.last_name = user.lastName;
  bm.firstName = user.firstName;
  bm.lastName = user.lastName;
  bm.email = user.email;
  bm.logoUrl = user.logo && user.logo.url;
  bm.role = getUserFriendlyRolesEnumeration({
    roles: user.roles,
    locale,
    roleContext: RoleContext.MEETING_APP,
    orgType,
    translateTexts: roleTranslateTexts,
  });
  bm.rolesForBoard = getUserFriendlyRolesEnumeration({
    roles: (user.roles || []).filter(
      (role) => _defaultRoleMap[role]?.CAN_VOTE_IN_THEORY_BOARDMEETING
    ),
    locale,
    roleContext: RoleContext.MEETING_APP,
    orgType,
    translateTexts: roleTranslateTexts,
  });
  bm.displayName = User.fullname(user);
  bm.roles = user.roles || [];
  bm.isDeletedFlag = !bm.roles.length;

  bm.votingright = hasPermission(PermissionEnum.CAN_VOTE_BOARDMEETING, bm.roles, roleMap);
  bm.canBeInvited = hasPermission(PermissionEnum.ACCESS_TO_BOARD_MEETINGS, bm.roles, roleMap);
  bm.canBeInvitedGA = hasPermission(PermissionEnum.ACCESS_TO_GA_MEETINGS, bm.roles, roleMap);
  bm.sortIndex = sortByRoleHelper({ roles: bm.roles });

  return bm;
}

export function isLikeSTI(type) {
  switch (type) {
    case 'STI':
    case 'FKF':
    case 'KF':
    case 'IKS':
    case 'IKSR':
      return true;
    default:
      return false;
  }
}

export function isMeetingGAType(meeting) {
  const type = meeting && meeting.meetingType;
  return (
    type === MeetingType.ANNUAL_GENERAL_MEETING ||
    type === MeetingType.EXTRAORDINARY_GENERAL_MEETING ||
    type === MeetingType.SHAREHOLDER_MEETING
  );
}

export type AltinnSubmitData = {
  data1: any;
  data2: any;
  shareholderData: any[];
  yearInfo: {
    year: string;
    dateStringFrom;
    dateStringTo;
  };
  post8: any[];
  post9: any[];
  post10: any[];
  post11: any[];
  post12: any[];
  post13: any[];
  shareClassInfo: {
    name: string;
    par_value: string;
    finalParValue: string;
    voteWeight: string;
  };
};

export enum OrgbrainGlobalEmailStatus {
  SKIPPED = 'SKIPPED',
  SENT = 'SENT',
  FAILED = 'FAILED',
}

type messagesStatusSchema = {
  email: string;
  userId: string;
  organizationId: string;
  messageId?: string;
  status: OrgbrainGlobalEmailStatus;
  additionalInfo?: string;
};
type messagesBatchSchema = {
  _id: string;
  userIds: string[];
  messagesStatuses?: messagesStatusSchema[];
  sendAttemptDate?: Date;
  batchIsFinishedAndErrorFree?: boolean;
};

export enum UserGroupTypeBulkEmail {
  ALL_USERS_ACTIVE_ORG = 'ALL_USERS_ACTIVE_ORG',
  ALL_USERS_INACTIVE_ORG = 'ALL_USERS_INACTIVE_ORG',
  ALL_USERS_PAYING_ORG = 'ALL_USERS_PAYING_ORG',
  ALL_USERS = 'ALL_USERS',
  BELONGS_TO_ORG = 'BELONGS_TO_ORG',
  BELONGS_TO_ORG_TYPE = 'BELONGS_TO_ORG_TYPE',
  BELONGS_TO_CLIENT = 'BELONGS_TO_CLIENT',
}

export class EmailMassSendingRestrictionSet {
  allWhoAcceptedTerms: boolean;
  allWhoAcceptedTermsDoubleCheck: boolean;
  typeOfUserGroup: UserGroupTypeBulkEmail;
  onlyOrgAdmins?: boolean;
  onlySuperUsers?: boolean;
  onlyMyself?: boolean;
  hubspotMarketingAccepting?: boolean;
  onlyRegisteredUsers?: boolean;
  onlyUnInvitedUnRegisteredUsers?: boolean;
  onlyInvitedUnRegisteredUsers?: boolean;
  allowedOrganizationNumbers?: string[];
  allowedOrganizationTypes?: string[];
  allowedClientIds?: string[];
}

export class OrgbrainGlobalEmailPayload extends EmailMassSendingRestrictionSet {
  _id?: string;
  isDraft?: boolean;
  deleted?: boolean;
  internalTitle: string;
  title: string;
  description: string;
  userIdsBatches: messagesBatchSchema[];
  allBatchesAreFinishedAndErrorFree?: boolean;
  locale: string;
  batchSize: number;
  excludeDeliveredFrom: string[];
  excludeSkippedFrom: string[];
  excludeFailedFrom: string[];
  isLocked?: boolean;
  createdAt: Date;
  modifiedBy: string;
}

export const TOO_BIG_FILE = 100000000;

export function trimStringToByteLength(inputString, maxByteLength) {
  inputString = typeof inputString === 'number' ? inputString.toString() : inputString || '';
  var byteLength = getByteLength(inputString);

  if (byteLength <= maxByteLength) {
    // No need to trim, the string is within the desired byte length
    return inputString;
  } else {
    var trimmedString = '';
    var currentByteLength = 0;

    for (var i = 0; i < inputString.length; ++i) {
      var charCode = inputString.charCodeAt(i);
      var charByteLength = 0;

      if (charCode < 0x80) {
        charByteLength = 1;
      } else if (charCode < 0x800) {
        charByteLength = 2;
      } else if (charCode < 0x10000) {
        charByteLength = 3;
      } else if (charCode < 0x200000) {
        charByteLength = 4;
      } else if (charCode < 0x4000000) {
        charByteLength = 5;
      } else {
        charByteLength = 6;
      }

      if (currentByteLength + charByteLength <= maxByteLength) {
        trimmedString += inputString[i];
        currentByteLength += charByteLength;
      } else {
        break; // Stop if adding the next character would exceed the limit
      }
    }

    return trimmedString;
  }
}

export function getByteLength(inputString) {
  inputString = typeof inputString === 'number' ? inputString.toString() : inputString || '';

  var totalBytes = 0;
  for (var i = 0; i < inputString.length; ++i) {
    var charCode = inputString.charCodeAt(i);
    if (charCode < 0x80) {
      totalBytes += 1;
    } else if (charCode < 0x800) {
      totalBytes += 2;
    } else if (charCode < 0x10000) {
      totalBytes += 3;
    } else if (charCode < 0x200000) {
      totalBytes += 4;
    } else if (charCode < 0x4000000) {
      totalBytes += 5;
    } else {
      totalBytes += 6;
    }
  }
  return totalBytes;
}

export type IHaveSeenSomethingInterestingPayload = {
  iHaveSeenTheWholeMeeting?: boolean;
  iHaveSeenAgendaItem?: boolean;
  iHaveSeenAgendaItemAttachment?: boolean;
  agendaItemId?: string;
  direntId?: string;
};

export enum USER_ACTIVITY_ACTIONS {
  MEETING_INVITE = 'MEETING_INVITE',
  MEETING_FINISH = 'MEETING_FINISH',
  SIGNING_INVITE = 'SIGNING_INVITE',
  MESSAGE_SEND = 'MESSAGE_SEND',
  TASK_CREATE = 'TASK_CREATE',
  SHAREHOLDERS_SHARE_REGISTER_EDIT = 'SHAREHOLDERS_SHARE_REGISTER_EDIT',
  SHAREHOLDERS_CAPITAL_INCREASE_CREATE = 'SHAREHOLDERS_CAPITAL_INCREASE_CREATE',
  SHAREHOLDERS_CAPITAL_INCREASE_COMPLETE = 'SHAREHOLDERS_CAPITAL_INCREASE_COMPLETE',
  SUBGROUP_CREATE = 'SUBGROUP_CREATE',
  TRANSACTION_DONE = 'TRANSACTION_DONE',
  SURVEY_INVITE = 'SURVEY_INVITE',
  ALTINN_RF1018_SUBMIT = 'ALTINN_RF1018_SUBMIT',
  BOARD_AUTHORIZATIONS_CREATED = 'BOARD_AUTHORIZATIONS_CREATED',
  FINANCIAL_CONTRACT_CREATED = 'FINANCIAL_CONTRACT_CREATED',
  BOARD_SCHOOL_MODULE_COMPLETED = 'BOARD_SCHOOL_MODULE_COMPLETED',
  BOARD_SCHOOL_EXAM_STARTED = 'BOARD_SCHOOL_EXAM_STARTED',
  BOARD_SCHOOL_EXAM_PASSED = 'BOARD_SCHOOL_EXAM_PASSED',
  TEMPLATE_DOCUMENT_CREATED = 'TEMPLATE_DOCUMENT_CREATED',
  ORGBRAIN_ACADEMY_VIEW_RESOURCES = 'ORGBRAIN_ACADEMY_VIEW_RESOURCES',
  SHARED_FOLDER_CREATED = 'SHARED_FOLDER_CREATED',
  FOLDER_CREATED = 'FOLDER_CREATED',
  PROXY_GIVEN = 'PROXY_GIVEN',
  MINUTES_DRAFT_SENT = 'MINUTES_DRAFT_SENT',
  MINUTES_SIGNING_SENT = 'MINUTES_SIGNING_SENT',
  SUBSCRIPTION_CHANGE = 'SUBSCRIPTION_CHANGE',
  SUBSCRIPTION_CANCEL = 'SUBSCRIPTION_CANCEL',
  USER_ADD = 'USER_ADD',
  USER_ROLE_CHANGED = 'USER_ROLE_CHANGED',
  ANNUAL_PLAN_CREATED = 'ANNUAL_PLAN_CREATED',
  AGENDA_ITEM_CUSTOM_CREATED = 'AGENDA_ITEM_CUSTOM_CREATED',
  AGENDA_SUGGESTION_CUSTOM_CREATED = 'AGENDA_SUGGESTION_CUSTOM_CREATED',
  TRYG_REQUEST_SENT = 'TRYG_REQUEST_SENT',
  BORD_MATCH_PROFILE_CREATED = 'BORD_MATCH_PROFILE_CREATED',
  BORD_MATCH_PROFILE_PUBLISHED = 'BORD_MATCH_PROFILE_PUBLISHED',
  BORD_MATCH_PROFILE_UNPUBLISHED = 'BORD_MATCH_PROFILE_UNPUBLISHED',
  JOB_ADVERTISEMENT_CREATED = 'JOB_ADVERTISEMENT_CREATED',
  JOB_ADVERTISEMENT_PUBLISHED = 'JOB_ADVERTISEMENT_PUBLISHED',
  JOB_ADVERTISEMENT_UNPUBLISHED = 'JOB_ADVERTISEMENT_UNPUBLISHED',
}

export type UserActivityStatistcs = {
  organizationId?: string;
  type: USER_ACTIVITY_ACTIONS;
  extraInfo?: string;
  userId: string;
  userEmail: string;
  userFullName: string;
  data?: any;
  rolesAtTheMomentOfSubmission: string[];
  orgType: string;
  orgName: string;
  subscription: string;
};
